document.addEventListener('DOMContentLoaded', function () {
    const LOCK_CART_QUANTITY_PROPERTY = '_lock_cart_quantity';

    function normalizeBooleanFlag(value) {
        if (value === true || value === 'true' || value === '1' || value === 1) {
            return true;
        }

        return false;
    }

    function getLockCartQuantityProperties(sourceElement = null) {
        if (!sourceElement) {
            return {};
        }

        const explicitFlag = sourceElement.dataset?.lockCartQuantity;
        if (normalizeBooleanFlag(explicitFlag)) {
            return {
                [LOCK_CART_QUANTITY_PROPERTY]: 'true'
            };
        }

        const parentForm = sourceElement.closest('form');
        const hiddenInput = parentForm?.querySelector(`[name="properties[${LOCK_CART_QUANTITY_PROPERTY}]"]`);
        if (normalizeBooleanFlag(hiddenInput?.value)) {
            return {
                [LOCK_CART_QUANTITY_PROPERTY]: 'true'
            };
        }

        return {};
    }

    function isQuantityLocked(item) {
        return normalizeBooleanFlag(item?.properties?.[LOCK_CART_QUANTITY_PROPERTY]);
    }

    // Shipping Protection: auto-added alongside real products, quantity-locked (via the existing
    // _lock_cart_quantity property, set on the product itself) and not directly removable -- it
    // only disappears once it's the only item left in the cart. window.ShippingProtectionVariantId
    // is injected server-side in layout/theme.liquid by looking up the 'shipping-protection' product.
    const SHIPPING_PROTECTION_VARIANT_ID = window.ShippingProtectionVariantId ? String(window.ShippingProtectionVariantId) : null;

    function isShippingProtectionItem(item) {
        return !!SHIPPING_PROTECTION_VARIANT_ID && String(item?.id) === SHIPPING_PROTECTION_VARIANT_ID;
    }

    // Keeps Shipping Protection in sync with the rest of the cart: only relevant for subscriptions,
    // so it's added/kept when the cart has a subscription selling plan (matching the plan of the
    // item that was just added, or whichever existing item has a plan otherwise), and removed
    // entirely for one-time-only carts.
    async function ensureShippingProtection(triggerSellingPlanId = null) {
        if (!SHIPPING_PROTECTION_VARIANT_ID) return;

        try {
            const cart = await fetch('/cart.js').then((res) => res.json());
            const otherItems = cart.items.filter((item) => !isShippingProtectionItem(item));
            if (!otherItems.length) return;

            let targetPlanId = triggerSellingPlanId ? String(triggerSellingPlanId) : null;
            if (!targetPlanId) {
                const subscriptionItem = otherItems.find((item) => item.selling_plan_allocation?.selling_plan?.id);
                if (subscriptionItem) {
                    targetPlanId = String(subscriptionItem.selling_plan_allocation.selling_plan.id);
                }
            }

            const protectionItem = cart.items.find(isShippingProtectionItem);

            if (!targetPlanId) {
                // One-time purchase, no subscription anywhere in the cart -- Shipping Protection
                // doesn't apply, so drop it if it's somehow still there.
                if (protectionItem) {
                    const lineNumber = cart.items.indexOf(protectionItem) + 1;
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ line: lineNumber, quantity: 0 }),
                    });
                }
                return;
            }

            const currentPlanId = protectionItem?.selling_plan_allocation?.selling_plan?.id
                ? String(protectionItem.selling_plan_allocation.selling_plan.id)
                : null;

            if (protectionItem && currentPlanId === targetPlanId) {
                return; // Already present on the right plan -- leave its line position alone.
            }

            if (protectionItem) {
                // Selling plan needs to change -- swap the line out for one on the correct plan.
                // Note: Shopify orders cart lines newest-first, so this necessarily makes the line
                // "newest" again (moves it to the top) until another product is added afterward.
                // There's no supported way to pin a line's position independent of insertion order.
                const lineNumber = cart.items.indexOf(protectionItem) + 1;
                await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ line: lineNumber, quantity: 0 }),
                });
            }

            const addBody = {
                id: Number(SHIPPING_PROTECTION_VARIANT_ID),
                quantity: 1,
                // Added via a raw API call (not the product form), so the usual hidden-input-driven
                // quantity lock needs to be set explicitly here to match.
                properties: { [LOCK_CART_QUANTITY_PROPERTY]: 'true' },
            };
            if (targetPlanId) addBody.selling_plan = targetPlanId;

            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(addBody),
            });
        } catch (error) {
            console.error('Error syncing Shipping Protection item:', error);
        }
    }

    // Shopify orders cart lines newest-first, so a line only stays at the bottom if it's the
    // OLDEST line in the cart. Called before a real product is added: if Shipping Protection isn't
    // in the cart yet, add it right now (with whatever selling plan the incoming product will use)
    // so that when the real product lands a moment later, it becomes the newer line and Shipping
    // Protection is left at the bottom -- indefinitely, since ensureShippingProtection() above
    // leaves its position alone once it's present on the right plan.
    async function ensureShippingProtectionBeforeAdd(triggerSellingPlanId = null) {
        if (!SHIPPING_PROTECTION_VARIANT_ID) return;

        try {
            const cart = await fetch('/cart.js').then((res) => res.json());
            if (cart.items.some(isShippingProtectionItem)) return; // Already present; leave it be.

            let targetPlanId = triggerSellingPlanId ? String(triggerSellingPlanId) : null;
            if (!targetPlanId) {
                const subscriptionItem = cart.items.find((item) => item.selling_plan_allocation?.selling_plan?.id);
                if (subscriptionItem) {
                    targetPlanId = String(subscriptionItem.selling_plan_allocation.selling_plan.id);
                }
            }
            if (!targetPlanId) return; // One-time purchase, no subscription in cart -- skip Shipping Protection.

            const addBody = {
                id: Number(SHIPPING_PROTECTION_VARIANT_ID),
                quantity: 1,
                properties: { [LOCK_CART_QUANTITY_PROPERTY]: 'true' },
                selling_plan: targetPlanId,
            };

            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(addBody),
            });
        } catch (error) {
            console.error('Error pre-adding Shipping Protection item:', error);
        }
    }

    // If Shipping Protection is the only thing left in the cart (every real product has been
    // removed), clear it too so the cart actually ends up empty.
    async function removeShippingProtectionIfAlone() {
        if (!SHIPPING_PROTECTION_VARIANT_ID) return;

        try {
            const cart = await fetch('/cart.js').then((res) => res.json());
            if (cart.items.length === 1 && isShippingProtectionItem(cart.items[0])) {
                await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ line: 1, quantity: 0 }),
                });
            }
        } catch (error) {
            console.error('Error clearing lone Shipping Protection item:', error);
        }
    }

    // Other add-to-cart entry points (landing page buyboxes, bundle landing pages) call these
    // directly since they don't share this file's addToCart() function.
    window.ensureShippingProtection = ensureShippingProtection;
    window.ensureShippingProtectionBeforeAdd = ensureShippingProtectionBeforeAdd;

    function getProductForm(sourceElement = null) {
        if (!sourceElement || typeof sourceElement.closest !== 'function') {
            return null;
        }

        return sourceElement.closest('form[data-product-form]') || sourceElement.closest('form') || null;
    }

    function resolveVariantId(sourceElement = null) {
        const parentForm = getProductForm(sourceElement);
        const buttonVariantId = sourceElement?.dataset?.variantId || sourceElement?.getAttribute?.('data-variant-id');
        const variantInputValue = parentForm?.querySelector('[name="id"]')?.value;
        const variantSelectionValue = parentForm?.querySelector('.variant-selection')?.getAttribute('variant');
        const candidateValues = [variantInputValue, variantSelectionValue, buttonVariantId];

        for (const value of candidateValues) {
            if (value && value !== 'not-selected' && value !== '123') {
                return value;
            }
        }

        return null;
    }

    function getAddToCartButton(sourceElement = null) {
        const parentForm = getProductForm(sourceElement);
        return parentForm?.querySelector('.product-form-add-to-cart') || null;
    }

    function updateVariantId(sourceElement = document) {
        const parentForm = getProductForm(sourceElement) || sourceElement?.querySelector?.('form[data-product-form]') || document.querySelector('form[data-product-form]');
        if (!parentForm) {
            return null;
        }

        const addToCartButton = parentForm.querySelector('.product-form-add-to-cart');
        if (!addToCartButton) {
            return null;
        }

        const selectedVariantId = resolveVariantId(parentForm);
        if (!selectedVariantId) {
            return null;
        }

        addToCartButton.dataset.variantId = selectedVariantId;
        return selectedVariantId;
    }


    // Handle add to cart button click for items in products query
    /*document.querySelectorAll('.item-add-to-cart-button').forEach(function (button) {
        // Find related variant selector and quantity picker
        const itemContainer = button.closest('.item-selectors');
        const variantSelect = itemContainer.querySelector('.item-variant-selector');
        const quantityInput = itemContainer.querySelector('.item-quantity-input');
        const quantityDecrement = itemContainer.querySelector('.item-quantity-button-decrement');
        const quantityIncrement = itemContainer.querySelector('.item-quantity-button-increment');

        // Update button's data-variant-id when variant is changed
        variantSelect.addEventListener('change', (e) => {
            button.dataset.variantId = e.target.value;
        });

        // Update button's data-quantity when quantity is changed
        quantityIncrement.addEventListener('click', () => {
            let qty = parseInt(quantityInput.value) + 1;
            quantityInput.value = qty;
            button.dataset.quantity = qty;
        });

        quantityDecrement.addEventListener('click', () => {
            let qty = Math.max(1, parseInt(quantityInput.value) - 1);
            quantityInput.value = qty;
            button.dataset.quantity = qty;
        });

        // Handle add to cart event
        button.addEventListener('click', (e) => {
            e.preventDefault();

            const variantId = button.dataset.variantId;
            const quantity = parseInt(button.dataset.quantity);

            addToCart(variantId, quantity, '', true, getLockCartQuantityProperties(button));
        });
    });*/

    // Support both the main site header and landing page header (.lp-header)
    const headers = document.querySelectorAll('header.site-header, .lp-header');
    const announcementBar = document.querySelector('.announcement-bar');

    function updateHeaderOnScroll() {
        const offset = announcementBar ? announcementBar.offsetHeight : 0;
        headers.forEach((header) => {
            if (!header) return;
            if (window.scrollY > offset) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }
        });
    }

    window.addEventListener('scroll', updateHeaderOnScroll, { passive: true });
    window.addEventListener('resize', updateHeaderOnScroll); // recalc on resize
    updateHeaderOnScroll(); // run once on page load








    document.body.addEventListener('click', function (e) {
        // Handle increment button
        if (e.target.matches('.item-quantity-button-increment')) {
            const itemContainer = e.target.closest('.item-selectors');
            const quantityInput = itemContainer.querySelector('.item-quantity-input');
            const button = itemContainer.querySelector('.item-add-to-cart-button');
            let qty = parseInt(quantityInput.value) + 1;
            quantityInput.value = qty;
            button.dataset.quantity = qty;
        }

        // Handle decrement button
        if (e.target.matches('.item-quantity-button-decrement')) {
            const itemContainer = e.target.closest('.item-selectors');
            const quantityInput = itemContainer.querySelector('.item-quantity-input');
            const button = itemContainer.querySelector('.item-add-to-cart-button');
            let qty = Math.max(1, parseInt(quantityInput.value) - 1);
            quantityInput.value = qty;
            button.dataset.quantity = qty;
        }

        // Handle add to cart button
        const button = e.target.closest('.item-add-to-cart-button');
        if (button) {
            e.preventDefault();
            const itemContainer = button.closest('.item-selectors');
            const variantSelect = itemContainer.querySelector('.item-variant-selector');
            const quantityInput = itemContainer.querySelector('.item-quantity-input');

            const variantId = variantSelect.value;
            const quantity = parseInt(quantityInput.value);

            addToCart(variantId, quantity);
        }
    });

    // Handle variant select change
    document.body.addEventListener('change', function (e) {
        if (e.target.matches('.item-variant-selector')) {
            const itemContainer = e.target.closest('.item-selectors');
            const button = itemContainer.querySelector('.item-add-to-cart-button');
            button.dataset.variantId = e.target.value;
        }
    });

    // Kenya AA landing button










    //const checkoutButton = document.getElementById('checkout-button');

    // Function to disable the button
    function disableCheckoutButton() {
        const checkoutButton = document.getElementById('checkout-button');
        if (checkoutButton) {
            checkoutButton.removeAttribute('data-href');
        }
    }

    // Function to enable the button again
    function enableCheckoutButton() {
        const checkoutButton = document.getElementById('checkout-button');
        if (checkoutButton) {
            checkoutButton.setAttribute('data-href', '/checkout');
        }
    }



    // Function to set a cookie with a specified expiration duration
    function setCookie(name, value, duration) {
        const d = new Date();
        d.setTime(d.getTime() + (duration * 24 * 60 * 60 * 1000)); // Expiration time in milliseconds
        const expires = "expires=" + d.toUTCString();
        document.cookie = name + "=" + value + ";" + expires + ";path=/";
    }

    // Function to get a cookie value by name
    function getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i].trim();
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    // Updated openCustomModal function with duration, closeModal, and cookieName parameter
    window.openCustomModal = function ({ title = '', subtitle = '', contentElement = '', duration = 30, modalClassName = '', cookieName } = {}) {
        // Check if duration is provided, and set default to 30 days if not
        if (!duration) duration = 30;  // Default duration to 30 days

        // Check if the modal has been closed before by looking for the cookie
        if (cookieName && getCookie(cookieName) === 'true') {
            console.log('❌ Modal has already been closed (cookie set). Skipping modal display.');
            return; // Exit the function without showing the modal
        }

        const modalElement = document.getElementById('customDynamicModal');

        // Remove all classes that start with "modal-"
        modalElement.classList.forEach(cls => {
            if (cls.startsWith('modal-')) {
                modalElement.classList.remove(cls);
            }
        });

        // Add the new class if provided
        if (modalClassName) {
            modalElement.classList.add(modalClassName);
        }

        const modalTitle = document.getElementById('custom-modal-title');
        const modalSubtitle = document.getElementById('custom-modal-subtitle');
        const modalBody = document.getElementById('custom-modal-body');

        if (modalTitle) modalTitle.innerHTML = title;
        if (modalSubtitle) modalSubtitle.innerHTML = subtitle;

        if (contentElement && document.querySelector(contentElement)) {
            modalBody.innerHTML = document.querySelector(contentElement).innerHTML;
        } else {
            modalBody.innerHTML = 'empty';
        }

        // Set the cookie to prevent the modal from showing again
        if (cookieName) {
            setCookie(cookieName, 'true', duration); // Cookie expires after the specified duration (in days)
        }

        // Show the modal
        document.getElementById('customDynamicModal').classList.add('show');
    };

    // Updated closeCustomModal function to handle the duration and cookieName parameters
    window.closeCustomModal = function () {
        document.getElementById('customDynamicModal').classList.remove('show');
    };

    // Event listener to close modal on 'Escape' key press
    // document.addEventListener('keydown', function (e) {
    //     if (e.key === 'Escape') window.closeCustomModal();
    // });





    /*
    // New quantity selector
    const quantitySelector = document.querySelector(".quantity-selector");
    const atcButton = document.querySelector(".product-form--atc-button");

    const valueElements = document.querySelectorAll(".quantity-value");
    const labelElements = document.querySelectorAll(".quantity-label");
    const priceElements = document.querySelectorAll(".quantity-price"); // select all price elements

    // Get the product price (assuming it's available as a global or inline JavaScript variable)
    const productPrice = parseFloat(document.querySelector("#product-price").value);
    
    const hiddenPurchaseOption = document.getElementById('purchase-option-hidden');
    const updateDisplay = (quantity) => {
        // Update quantity and label
        valueElements.forEach(el => el.textContent = quantity);
        labelElements.forEach(el => el.textContent = quantity === 1 ? "bag" : "bags");
    
        // Check if subscription is selected
        const isSubscription = hiddenPurchaseOption?.value === 'subscription';
    
        // Calculate price (10% discount if subscription)
        const unitPrice = isSubscription ? productPrice * 0.9 : productPrice;
        const totalPrice = (unitPrice * quantity / 100).toFixed(2); // assuming price is in cents
    
        // Update price
        priceElements.forEach(el => el.textContent = "" + totalPrice);
    
        // Update the data-quantity on the Add to Cart button
        if (atcButton) {
            atcButton.setAttribute("data-quantity", quantity);
        }
    };

    quantitySelector.querySelectorAll(".quantity-selector-button").forEach(button => {
        button.addEventListener("click", () => {
            let quantity = parseInt(valueElements[0].textContent); // use the first one as reference

            if (button.classList.contains("bi-plus")) {
                quantity++;
            } else if (button.classList.contains("bi-dash")) {
                quantity = Math.max(1, quantity - 1);
            }

            updateDisplay(quantity);
        });
    });

    // Initialize on load
    updateDisplay(parseInt(valueElements[0].textContent));

    */

    function initializeProductForm(modalRoot = document) {
        const options = modalRoot.querySelectorAll('.custom-subscription-widget-option');
        const hiddenPurchaseOption = modalRoot.querySelector('#purchase-option-hidden');
        const subscriptionPlans = modalRoot.querySelector('#subscription-plans');
        const sellingPlanSelect = modalRoot.querySelector('#selling-plan-select');
        const sellingPlanHiddenInput = modalRoot.querySelector('#selected-selling-plan-id');
        const widget = modalRoot.querySelector('#custom-subscription-widget');

        const quantitySelector = modalRoot.querySelector(".quantity-selector");
        const step3Cards = modalRoot.querySelectorAll('[data-step-3-card]');
        const atcButton = modalRoot.querySelector(".product-form--atc-button");
        const atcText = modalRoot.querySelector('.atc-button-text');

        const valueElements = modalRoot.querySelectorAll(".quantity-value");
        const labelElements = modalRoot.querySelectorAll(".quantity-label");
        const priceElements = modalRoot.querySelectorAll(".quantity-price");
        const productPriceInput = modalRoot.querySelector("#product-price");
        if (!productPriceInput) return;

        const productPrice = parseFloat(productPriceInput.value);
        // dataset access doesn't work here: browsers only camelCase a hyphen when it's followed
        // by a letter, so data-pack-discount-3 lands at dataset["packDiscount-3"], not
        // dataset.packDiscount3 -- getAttribute reads the literal attribute name instead.
        const packDiscount3 = Number(modalRoot.querySelector('[data-step-3-cards]')?.getAttribute('data-pack-discount-3') || 3) * 100;
        const packDiscount6 = Number(modalRoot.querySelector('[data-step-3-cards]')?.getAttribute('data-pack-discount-6') || 4) * 100;

        // Ensure hiddenPurchaseOption matches the default active option on page load
        const defaultOption = modalRoot.querySelector('.custom-subscription-widget-option.active');
        if (defaultOption) {
            const selectedOption = defaultOption.getAttribute('data-option');
            if (hiddenPurchaseOption) hiddenPurchaseOption.value = selectedOption;
            setPurchaseSelection(selectedOption === 'subscription' ? 'subscription' : 'onetime');
        }

        function updateAtcButtonText() {
            if (!atcText) return;
            const val = hiddenPurchaseOption?.value || modalRoot.querySelector('.custom-subscription-widget-option.active')?.dataset.option;
            if (val === 'subscription') {
                atcText.textContent = 'Start subscription';
            } else {
                atcText.textContent = 'add to cart';
            }
        }

        function isSubscriptionSelected() {
            return hiddenPurchaseOption?.value === 'subscription';
        }

        function getSelectedQuantity() {
            const selectedCard = modalRoot.querySelector('.items-quantity-selector__item--selected, [data-step-3-card].is-selected');
            const quantityValue = selectedCard?.dataset?.quantity || selectedCard?.dataset?.qty || "1";
            return Math.max(1, parseInt(quantityValue, 10) || 1);
        }

        function setSelectedQuantity(quantity) {
            step3Cards.forEach((card) => {
                const cardQuantity = parseInt(card.dataset.quantity || card.dataset.qty || "0", 10);
                const isSelected = cardQuantity === quantity;
                card.classList.toggle('items-quantity-selector__item--selected', isSelected);
                card.classList.toggle('is-selected', isSelected);
            });
        }

        function setPurchaseSelection(purchaseType) {
            modalRoot.querySelectorAll('[data-purchase-card]').forEach((card) => {
                const isSelected = (card.getAttribute('data-purchase-type') || '') === purchaseType;
                card.classList.toggle('is-selected', isSelected);
            });
        }

        function calcLineCents(basePerBagCents, qty) {
            const baseLineCents = basePerBagCents * qty;
            return isSubscriptionSelected() ? Math.round(baseLineCents * 0.9) : baseLineCents;
        }

        function updateStep3CardPrices(quantity) {
            step3Cards.forEach((card) => {
                const cardQuantity = parseInt(card.dataset.quantity || card.dataset.qty || "1", 10) || 1;
                const packDiscount = cardQuantity === 3 ? packDiscount3 : cardQuantity === 6 ? packDiscount6 : 0;
                const basePerBagCents = Math.max(productPrice - packDiscount, 0);
                const totalLineCents = calcLineCents(basePerBagCents, cardQuantity);
                const perBagCents = isSubscriptionSelected()
                    ? Math.round(basePerBagCents * 0.9)
                    : basePerBagCents;
                const totalPrice = (totalLineCents / 100).toFixed(2);
                const perBagPrice = (perBagCents / 100).toFixed(2);
                const totalNode = card.querySelector('[data-step3-total]');
                const perBagNode = card.querySelector('[data-step3-perbag]');
                if (totalNode) totalNode.textContent = `$${totalPrice}`;
                if (perBagNode) perBagNode.textContent = `$${perBagPrice}`;

                // "SAVE $X" vs. paying full one-time price per bag -- reflects both the bundle
                // discount and the subscription discount together, whichever apply right now.
                // 3/6-bag bundles also ship free, so add the $7.95 a single bag would've cost in
                // shipping to the displayed savings (label only, no cart/checkout impact).
                const savingsNode = card.querySelector('[data-step3-savings]');
                if (savingsNode) {
                    const fullPriceCents = productPrice * cardQuantity;
                    const shippingSavingsCents = (cardQuantity === 3 || cardQuantity === 6) ? 795 : 0;
                    const savingsCents = Math.max(fullPriceCents - totalLineCents, 0) + shippingSavingsCents;
                    savingsNode.textContent = (savingsCents / 100).toFixed(2);
                }
            });

            if (priceElements.length) {
                const selectedCard = modalRoot.querySelector('.items-quantity-selector__item--selected, [data-step-3-card].is-selected');
                const selectedQuantity = parseInt(selectedCard?.dataset?.quantity || selectedCard?.dataset?.qty || String(quantity), 10) || 1;
                const selectedPackDiscount = selectedQuantity === 3 ? packDiscount3 : selectedQuantity === 6 ? packDiscount6 : 0;
                const selectedBasePerBagCents = Math.max(productPrice - selectedPackDiscount, 0);
                const currentPrice = (calcLineCents(selectedBasePerBagCents, selectedQuantity) / 100).toFixed(2);
                priceElements.forEach(el => el.textContent = currentPrice);
            }
        }

        function updateSellingPlanDataAttribute() {
            const selectedPlanId = sellingPlanSelect?.value;
            if (selectedPlanId && isSubscriptionSelected()) {
                widget?.setAttribute('data-selected-selling-plan', selectedPlanId);
                if (sellingPlanHiddenInput) sellingPlanHiddenInput.value = selectedPlanId;
            }
        }

        function updateDisplay(quantity) {
            valueElements.forEach(el => el.textContent = quantity);
            labelElements.forEach(el => el.textContent = quantity === 1 ? "bag" : "bags");
            setSelectedQuantity(quantity);
            updateStep3CardPrices(quantity);

            if (atcButton) {
                atcButton.setAttribute("data-quantity", quantity);
                // Price no longer shown on the CTA button -- was: build "ADD TO CART - $X.XX"
                // and drive it into the button's ::after via a CSS custom property.
                // const currentPrice = priceElements[0]?.textContent || "0.00";
                // const labelText = `ADD TO CART - $${currentPrice}`;
                // atcButton.style.setProperty("--dynamic-label", `"${labelText}"`);
            }
        }

        // Initial setup
        updateSellingPlanDataAttribute();
        let currentQuantity = getSelectedQuantity();
        updateDisplay(currentQuantity);
        // Ensure the subscription delivery schedule is visible by default
        // if (subscriptionPlans) subscriptionPlans.style.display = '';

        // Handle subscription/one-time toggle
        options.forEach((option) => {
            option.addEventListener('click', () => {
                options.forEach((o) => o.classList.remove('active'));
                option.classList.add('active');

                const selectedOption = option.getAttribute('data-option');
                if (hiddenPurchaseOption) hiddenPurchaseOption.value = selectedOption;

                if (selectedOption === 'subscription') {
                    // if (subscriptionPlans) subscriptionPlans.style.display = 'block';
                    updateSellingPlanDataAttribute();
                } else {
                    widget?.removeAttribute('data-selected-selling-plan');
                    if (sellingPlanHiddenInput) sellingPlanHiddenInput.value = '';
                }

                setPurchaseSelection(selectedOption);
                const newQuantity = getSelectedQuantity();
                updateDisplay(newQuantity);
                updateAtcButtonText();
            });
        });

        // Update on dropdown change
        sellingPlanSelect?.addEventListener('change', () => {
            updateSellingPlanDataAttribute();
            const newQuantity = getSelectedQuantity();
            updateDisplay(newQuantity);
            updateAtcButtonText();
        });

        // Quantity change handlers
        quantitySelector?.querySelectorAll(".quantity-selector-button").forEach(button => {
            button.addEventListener("click", () => {
                let quantity = parseInt(valueElements[0]?.textContent || "1");

                if (button.classList.contains("quantity-selector-button-plus")) {
                    quantity++;
                } else if (button.classList.contains("quantity-selector-button-minus")) {
                    quantity = Math.max(1, quantity - 1);
                }

                updateDisplay(quantity);
            });
        });

        step3Cards.forEach((card) => {
            card.addEventListener('click', () => {
                if (card.getAttribute('aria-disabled') === 'true') return;
                const quantity = parseInt(card.dataset.quantity || card.dataset.qty || "1", 10) || 1;
                updateDisplay(quantity);
            });
        });
    }

    initializeProductForm();







    const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
            if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1 && node.id === 'custom-subscription-widget') {
                        initializeProductForm(document);
                    }

                    // In case it's nested deeper:
                    if (node.nodeType === 1 && node.querySelector && node.querySelector('#custom-subscription-widget')) {
                        initializeProductForm(document);
                    }
                });
            }
        }
    });

    // Start observing body (or a static parent that will always be in the DOM)
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });


    function toggleCart(open = true) {
        const cart = document.getElementById('side-cart');
        const backdrop = document.getElementById('side-cart-backdrop');
        if (!cart) return;

        if (open) {
            cart.classList.add('open');
            if (backdrop) backdrop.classList.add('visible');
            document.body.classList.add('side-cart-open');
        } else {
            cart.classList.remove('open');
            if (backdrop) backdrop.classList.remove('visible');
            document.body.classList.remove('side-cart-open');
        }
    }



    // Toggle Side Cart
    document.querySelector('.site-header-cart--button').addEventListener('click', (event) => {
        event.preventDefault(); // Prevent default link behavior
        toggleCart(true); // Open the side cart
        loadCart();
    });

    // Close Side Cart
    document.getElementById('close-cart').addEventListener('click', () => {
        toggleCart(false); // Close the side cart
    });

    // const sideCartBackdrop = document.getElementById('side-cart-backdrop');
    // if (sideCartBackdrop) {
    //     sideCartBackdrop.addEventListener('click', () => toggleCart(false));
    // }

    // Load Cart Contents
    async function loadCart() {
        showOverlay(); // Show overlay when loading cart
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Failed to load cart data');
            const cart = await response.json();

            // Shipping Protection always renders last, regardless of when it was actually added
            // to the cart (new lines are otherwise appended in whatever order Shopify returns them).
            cart.items.sort((a, b) => {
                const aIsProtection = isShippingProtectionItem(a) ? 1 : 0;
                const bIsProtection = isShippingProtectionItem(b) ? 1 : 0;
                return aIsProtection - bIsProtection;
            });

            // Shipping Protection is added to the cart (and does show up at checkout) but is
            // intentionally left out of the side cart's item list and totals entirely, so it
            // doesn't skew what's shown here vs. what the cart-level fields below already include.
            const protectionItem = cart.items.find(isShippingProtectionItem);
            const protectionOriginalLine = protectionItem
                ? (protectionItem.original_line_price ?? protectionItem.line_price ?? (protectionItem.price * protectionItem.quantity))
                : 0;
            const protectionFinalLine = protectionItem
                ? (protectionItem.final_line_price ?? protectionItem.line_price ?? (protectionItem.price * protectionItem.quantity))
                : 0;
            const protectionLineSavings = Math.max(0, protectionOriginalLine - protectionFinalLine);
            const visibleItems = cart.items.filter((item) => !isShippingProtectionItem(item));

            let cartItems = '';
            const originalSubtotal = (cart.original_total_price ?? cart.items.reduce((sum, item) => {
                const base = item.original_line_price ?? item.line_price ?? (item.price * item.quantity);
                return sum + base;
            }, 0)) - protectionOriginalLine;
            let totalItemPrice = originalSubtotal; // Items without any discount
            let totalDiscount = (cart.total_discount || 0) - protectionLineSavings; // Cart-level discount total from Shopify
            let finalTotal = (cart.total_price || Math.max(totalItemPrice - totalDiscount, 0)) - protectionFinalLine; // Final total after discounts (product-only)
            let totalQuantity = 0;
            let bundleSavingsTotal = 0;
            let subscriptionSavingsTotal = 0;
            // Calculate total item price and total quantity
            let _lastSubtitleKey = null;

            // Pre-compute total line-level savings so we can isolate the cart-code discount
            const totalLineSavings = visibleItems.reduce((sum, item) => {
                const orig = item.original_line_price ?? item.line_price ?? (item.price * item.quantity);
                const fin = item.final_line_price ?? item.line_price ?? (item.price * item.quantity);
                return sum + Math.max(0, orig - fin);
            }, 0);
            // Discount from cart-level codes that isn't already reflected in final_line_price
            const extraCartDiscount = Math.max(0, totalDiscount - totalLineSavings);

            // Calculate total item price and total quantity
            visibleItems.forEach(item => {
                const originalLine = item.original_line_price ?? item.line_price ?? (item.price * item.quantity);
                const finalLine = item.final_line_price ?? item.line_price ?? (item.price * item.quantity);
                const lineSavings = Math.max(0, originalLine - finalLine);
                // Proportional share of cart-code discount for this item
                const itemCartShare = (extraCartDiscount > 0 && totalItemPrice > 0)
                    ? Math.round(extraCartDiscount * (originalLine / totalItemPrice))
                    : 0;
                const combinedSavings = lineSavings + itemCartShare;
                const adjustedFinalLine = finalLine - itemCartShare;
                const hasDiscount = combinedSavings > 0;

                // Check if subscription (selling plan) exists for this item
                const sellingPlanName = item.selling_plan_allocation?.selling_plan?.name
                    ?.replace(/^Subscription:\s*/i, '') // drop the "Subscription: " prefix
                    ?.replace(/^Ships\s+/i, '') // drop the leading "Ships " too, e.g. "every 4 weeks"
                    ?.replace(/^./, (c) => c.toUpperCase()); // re-capitalize the new first letter
                const showSubscription = !!sellingPlanName; // Only show if sellingPlanId exists
                const quantityLocked = isQuantityLocked(item);
                const quantityButtonState = quantityLocked ? ' disabled aria-disabled="true" tabindex="-1"' : '';
                const quantityControlsClassName = quantityLocked ? 'quantity-controls is-locked' : 'quantity-controls tw:border tw:border-[#A26A21] tw:rounded-brand tw:p-[7px]';

                // If Shopify didn’t provide cart.total_discount, fall back to computed savings
                if (!cart.total_discount && lineSavings > 0) {
                    totalDiscount += lineSavings;
                    finalTotal = Math.max(totalItemPrice - totalDiscount, 0);
                }

                // <div class="side-cart-item-subtitle">${item.sku}: ${item.variant_title === "Whole Beans" ? "Beans" : item.variant_title}</div>
                //                 <div class="side-cart-item-subtitle">${item.variant_title}</div>


                // ${caffeineContent === 'Decaf' 
                //     ? `<div class="side-cart-item-subtitle">${subtitle}</div>` 
                //     : ''}

                // Deduplicate variant subtitle parts (e.g. "Whole Bean / Whole Bean / Whole Bean" -> "Whole Bean")
                let _subtitle = item.variant_title || '';
                _subtitle = _subtitle.split('·')[0].trim(); // Drop everything after "·", e.g. "Espresso Grind · 12 oz/bag" -> "Espresso Grind"
                const _parts = _subtitle.split('/').map(p => p.trim()).filter(Boolean);
                const _unique = _parts.filter((v, i, a) => a.indexOf(v) === i);
                const _finalSubtitle = _unique.join(' / ');

                // Bundles are sold three different ways: the product page adds N units of the
                // same variant (item.quantity is 3 or 6), the landing page adds 1 unit of a
                // dedicated same-flavor bundle product instead (item.quantity stays 1, but
                // item.handle identifies it, e.g. "classico-espresso-3-bags-bundle"), and the
                // collections-page mixed bundles (Trio, Day & Night, Signature Collection --
                // real Shopify Bundles-app composite products, e.g. "the-espresso-trio") are
                // also added as qty 1 but carry no such handle suffix, so they're identified by
                // the _bundle_contents/_bundle_bag_count line-item properties set at add-to-cart
                // time instead (see espresso-bundle-quickshop.liquid's trigger + the
                // initBundleQuickshop add-to-cart handler in espresso-landing.js).
                const bagQuantity = item.quantity;
                const handle = item.handle || '';
                const bundleContents = item.properties && item.properties['_bundle_contents'];
                const bundleBagCountProperty = item.properties && item.properties['_bundle_bag_count'];
                let isBundle = bagQuantity === 3 || bagQuantity === 6;
                let bundleBagCount = bagQuantity;
                if (!isBundle && handle.includes('3-bags-bundle')) {
                    isBundle = true;
                    bundleBagCount = 3;
                } else if (!isBundle && handle.includes('6-bags-bundle')) {
                    isBundle = true;
                    bundleBagCount = 6;
                } else if (!isBundle && bundleContents) {
                    isBundle = true;
                    const parsedBagCount = parseInt(bundleBagCountProperty || '', 10);
                    bundleBagCount = Number.isFinite(parsedBagCount) && parsedBagCount > 0 ? parsedBagCount : bagQuantity;
                }
                const bagLabel = isBundle
                    ? `${bundleBagCount} Bag Bundle`
                    : `${bagQuantity} ${bagQuantity === 1 ? 'bag' : 'bags'}`;
                const bagLabelClass = isBundle ? '' : ' tw:capitalize';
                const bundleContentsHtml = bundleContents
                    ? `<div class="side-cart-item-bundle-contents tw:mt-1 tw:text-sm tw:text-[#5c5c5e]">${bundleContents}</div>`
                    : '';

                // Shopify tracks bundle ("...Promotion...") and subscription ("Subscription 10% OFF")
                // discounts as separate, itemized entries in item.discounts — sum them by keyword.
                if (Array.isArray(item.discounts)) {
                    item.discounts.forEach((d) => {
                        if (/subscription/i.test(d.title)) {
                            subscriptionSavingsTotal += d.amount;
                        } else if (/promotion/i.test(d.title)) {
                            bundleSavingsTotal += d.amount;
                        }
                    });
                }
                // Landing-page bundle products (separate SKU, e.g. "...-3-bags-bundle") are priced
                // flat-lower instead of getting a tracked Promotion discount, so estimate their
                // savings using the same per-bag rate the real on-variant promotions confirm
                // ($2/bag for 3-bag bundles, $3/bag for 6-bag bundles).
                if (isBundle && handle.includes('bags-bundle')) {
                    const hasTrackedBundleDiscount = item.discounts?.some((d) => /promotion/i.test(d.title));
                    if (!hasTrackedBundleDiscount) {
                        const perBagCents = bundleBagCount === 6 ? 300 : 200;
                        bundleSavingsTotal += perBagCents * bundleBagCount * item.quantity;
                    }
                }

                cartItems +=
                    `<div class="side-cart-item tw:border tw:border-[#e4e4e4]" data-variant-id="${item.id}">
                        <div class="side-cart-item-inner">
                            <div class="side-cart-item-image-wrapper">
                                <img src="${item.image}" alt="${item.title}" class="side-cart-item-image" />
                            </div>

                            <div class="side-cart-item-info">
                                <div class="side-cart-item-title">${item.product_title}</div>
                                <div class="side-cart-item-quantity-label tw:mb-2 tw:font-semibold tw:text-[#A26A21]${bagLabelClass}">${bagLabel}</div>
                                ${bundleContentsHtml}
                                ${(() => {
                        const hasSubtitle = !!_finalSubtitle;
                        const hasSubscription = !!sellingPlanName;
                        const subtitleDividerClass = (hasSubtitle && hasSubscription) ? ' tw:sm:border-r tw:sm:border-[#e4e4e4] tw:pr-2' : '';
                        const subtitleHtml = hasSubtitle
                            ? `<div class="side-cart-item-subtitle tw:text-sm tw:flex tw:items-center tw:gap-2${subtitleDividerClass}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-cup-hot" viewBox="0 0 16 16">
  <path fill-rule="evenodd" d="M.5 6a.5.5 0 0 0-.488.608l1.652 7.434A2.5 2.5 0 0 0 4.104 16h5.792a2.5 2.5 0 0 0 2.44-1.958l.131-.59a3 3 0 0 0 1.3-5.854l.221-.99A.5.5 0 0 0 13.5 6zM13 12.5a2 2 0 0 1-.316-.025l.867-3.898A2.001 2.001 0 0 1 13 12.5M2.64 13.825 1.123 7h11.754l-1.517 6.825A1.5 1.5 0 0 1 9.896 15H4.104a1.5 1.5 0 0 1-1.464-1.175"/>
  <path d="m4.4.8-.003.004-.014.019a4 4 0 0 0-.204.31 2 2 0 0 0-.141.267c-.026.06-.034.092-.037.103v.004a.6.6 0 0 0 .091.248c.075.133.178.272.308.445l.01.012c.118.158.26.347.37.543.112.2.22.455.22.745 0 .188-.065.368-.119.494a3 3 0 0 1-.202.388 5 5 0 0 1-.253.382l-.018.025-.005.008-.002.002A.5.5 0 0 1 3.6 4.2l.003-.004.014-.019a4 4 0 0 0 .204-.31 2 2 0 0 0 .141-.267c.026-.06.034-.092.037-.103a.6.6 0 0 0-.09-.252A4 4 0 0 0 3.6 2.8l-.01-.012a5 5 0 0 1-.37-.543A1.53 1.53 0 0 1 3 1.5c0-.188.065-.368.119-.494.059-.138.134-.274.202-.388a6 6 0 0 1 .253-.382l.025-.035A.5.5 0 0 1 4.4.8m3 0-.003.004-.014.019a4 4 0 0 0-.204.31 2 2 0 0 0-.141.267c-.026.06-.034.092-.037.103v.004a.6.6 0 0 0 .091.248c.075.133.178.272.308.445l.01.012c.118.158.26.347.37.543.112.2.22.455.22.745 0 .188-.065.368-.119.494a3 3 0 0 1-.202.388 5 5 0 0 1-.253.382l-.018.025-.005.008-.002.002A.5.5 0 0 1 6.6 4.2l.003-.004.014-.019a4 4 0 0 0 .204-.31 2 2 0 0 0 .141-.267c.026-.06.034-.092.037-.103a.6.6 0 0 0-.09-.252A4 4 0 0 0 6.6 2.8l-.01-.012a5 5 0 0 1-.37-.543A1.53 1.53 0 0 1 6 1.5c0-.188.065-.368.119-.494.059-.138.134-.274.202-.388a6 6 0 0 1 .253-.382l.025-.035A.5.5 0 0 1 7.4.8m3 0-.003.004-.014.019a4 4 0 0 0-.204.31 2 2 0 0 0-.141.267c-.026.06-.034.092-.037.103v.004a.6.6 0 0 0 .091.248c.075.133.178.272.308.445l.01.012c.118.158.26.347.37.543.112.2.22.455.22.745 0 .188-.065.368-.119.494a3 3 0 0 1-.202.388 5 5 0 0 1-.252.382l-.019.025-.005.008-.002.002A.5.5 0 0 1 9.6 4.2l.003-.004.014-.019a4 4 0 0 0 .204-.31 2 2 0 0 0 .141-.267c.026-.06.034-.092.037-.103a.6.6 0 0 0-.09-.252A4 4 0 0 0 9.6 2.8l-.01-.012a5 5 0 0 1-.37-.543A1.53 1.53 0 0 1 9 1.5c0-.188.065-.368.119-.494.059-.138.134-.274.202-.388a6 6 0 0 1 .253-.382l.025-.035A.5.5 0 0 1 10.4.8"/>
</svg>${_finalSubtitle}</div>`
                            : '';
                        const subscriptionHtml = hasSubscription
                            ? `<div class="side-cart-item-subscription tw:text-sm tw:flex tw:items-center tw:gap-2"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-calendar" viewBox="0 0 16 16">
  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z"/>
</svg>${sellingPlanName}</div>`
                            : '';
                        if (!subtitleHtml && !subscriptionHtml) return '';
                        return `<div class="side-cart-item-meta tw:flex tw:flex-col tw:sm:flex-row tw:sm:gap-2">${subtitleHtml}${subscriptionHtml}</div>`;
                    })()}

                            </div>

                            <div class="side-cart-item-right tw:flex tw:flex-col tw:items-end tw:gap-2">
                                <div class="side-cart-item-actions tw:flex tw:items-center tw:gap-2">
                                    <div class="${quantityControlsClassName}" data-quantity-locked="${quantityLocked ? 'true' : 'false'}">
                                    <button class="quantity-action quantity-decrease  tw:bg-[#f6f1ec] tw:border tw:border-[#e8dbc8]" data-variant-id="${item.id}" data-selling-plan-id="${item.selling_plan_allocation?.selling_plan?.id || ''}" data-quantity-locked="${quantityLocked ? 'true' : 'false'}"${quantityButtonState}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-dash tw:text-[#A26A21]" viewBox="0 0 16 16">
  <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8"/>
</svg></button>
                                    <span class="quantity">${item.quantity}</span>
                                    <button class="quantity-action quantity-increase tw:bg-[#f6f1ec] tw:border tw:border-[#e8dbc8]" data-variant-id="${item.id}" data-selling-plan-id="${item.selling_plan_allocation?.selling_plan?.id || ''}" data-quantity-locked="${quantityLocked ? 'true' : 'false'}"${quantityButtonState}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-plus-lg tw:text-[#A26A21]" viewBox="0 0 16 16">
  <path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/>
</svg></button>
                                    </div>
                                    <button type="button" class="side-cart-remove" aria-label="Remove item" data-variant-id="${item.id}" data-selling-plan-id="${item.selling_plan_allocation?.selling_plan?.id || ''}"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16"><path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5"/></svg></button>
                                </div>
                                <div class="side-cart-item-price-stack tw:flex tw:flex-col tw:items-end">
                                    ${hasDiscount ? `<div class="side-cart-original-price tw:text-[#5c5c5e]" style="text-decoration: line-through;">$${(originalLine / 100).toFixed(2)}</div>` : ''}
                                    <div class="side-cart-item-total tw:text-xl">$${(adjustedFinalLine / 100).toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                        ${hasDiscount ? `<div class="side-cart-savings tw:mt-2 tw:text-[#A26A21]">${isBundle ? `${bagLabel} - ` : ''}Save $${(combinedSavings / 100).toFixed(2)}</div>` : ''}
                    </div>`;
                totalQuantity += item.quantity; // Accumulate total quantity
            });

            const isCartEmpty = !cart.items || cart.items.length === 0 || totalQuantity === 0;

            // Determine shipping charge for display: FREE when discount exists or total ≥ $49, otherwise $7.95. No shipping on empty cart.
            const shippingThreshold = 4900; // $49.00 in cents
            const shippingCharge = isCartEmpty
                ? 0
                : (totalDiscount > 0 || finalTotal >= shippingThreshold) ? 0 : 795;

            const displayTotal = isCartEmpty ? 0 : finalTotal + shippingCharge;

            // Update the DOM with the calculated values
            document.getElementById('cart-items').innerHTML = cartItems;

            // Subtotal (before discounts)
            const cartSubtotalValueEl = document.querySelector('#cart-subtotal .cart-value');
            if (cartSubtotalValueEl) {
                cartSubtotalValueEl.textContent = `$${(totalItemPrice / 100).toFixed(2)}`;
            } else {
                const subtotalEl = document.getElementById('cart-subtotal');
                if (subtotalEl) subtotalEl.textContent = `Items: $${(totalItemPrice / 100).toFixed(2)}`;
            }

            // Total (after discounts + shipping display charge)
            const cartTotalValueEl = document.querySelector('#cart-total .cart-value');
            if (cartTotalValueEl) {
                cartTotalValueEl.textContent = `$${(displayTotal / 100).toFixed(2)}`;
            } else {
                document.getElementById('cart-total').textContent = `Total: $${(displayTotal / 100).toFixed(2)}`; // Update total price
            }

            document.getElementById('total-quantity').textContent = totalQuantity; // Update total quantity


            // Handle discount visibility and value
            const discountElement = document.getElementById('cart-discount');
            const shippingElement = document.getElementById('cart-shipping');
            if (discountElement) {
                if (totalDiscount > 0) {
                    const discountValueEl = discountElement.querySelector('.cart-value');
                    const formatted = `-$${(totalDiscount / 100).toFixed(2)}`;
                    if (discountValueEl) {
                        discountValueEl.textContent = formatted;
                    } else {
                        // Fallback for older markup
                        discountElement.textContent = `Discount: ${formatted}`;
                    }
                    // Show the whole discount block. inner wrapper handles row layout, so use block.
                    discountElement.style.display = 'block';
                } else {
                    // Clear only the value span (if present) and hide the entire discount block.
                    const discountValueEl = discountElement.querySelector('.cart-value');
                    if (discountValueEl) discountValueEl.textContent = '';
                    // Hide the whole discount container so layout collapses
                    discountElement.style.display = 'none';
                }
            }

            // Shipping display: hide on empty cart; FREE if discount or total ≥ threshold; otherwise $6.95.
            if (shippingElement) {
                if (isCartEmpty) {
                    const shippingValueEl = shippingElement.querySelector('.cart-value');
                    if (shippingValueEl) shippingValueEl.textContent = '$0.00';
                    shippingElement.style.display = 'none';
                } else if (totalDiscount > 0 || finalTotal >= shippingThreshold) {
                    const shippingValueEl = shippingElement.querySelector('.cart-value');
                    if (shippingValueEl) shippingValueEl.textContent = 'FREE';
                    shippingElement.style.display = 'flex';
                } else {
                    const shippingValueEl = shippingElement.querySelector('.cart-value');
                    if (shippingValueEl) shippingValueEl.textContent = '$7.95';
                    shippingElement.style.display = 'flex';
                }
            }

            // Bundle Savings: real "...Promotion..." discounts plus estimated landing-page-bundle savings
            const bundleSavingsElement = document.getElementById('cart-bundle-savings');
            if (bundleSavingsElement) {
                if (bundleSavingsTotal > 0) {
                    const bundleSavingsValueEl = bundleSavingsElement.querySelector('.cart-value');
                    if (bundleSavingsValueEl) bundleSavingsValueEl.textContent = `-$${(bundleSavingsTotal / 100).toFixed(2)}`;
                    bundleSavingsElement.style.display = 'block';
                } else {
                    const bundleSavingsValueEl = bundleSavingsElement.querySelector('.cart-value');
                    if (bundleSavingsValueEl) bundleSavingsValueEl.textContent = '';
                    bundleSavingsElement.style.display = 'none';
                }
            }

            // Subscription Savings: real "Subscription 10% OFF" discounts
            const subscriptionSavingsElement = document.getElementById('cart-subscription-savings');
            if (subscriptionSavingsElement) {
                if (subscriptionSavingsTotal > 0) {
                    const subscriptionSavingsValueEl = subscriptionSavingsElement.querySelector('.cart-value');
                    if (subscriptionSavingsValueEl) subscriptionSavingsValueEl.textContent = `-$${(subscriptionSavingsTotal / 100).toFixed(2)}`;
                    subscriptionSavingsElement.style.display = 'block';
                } else {
                    const subscriptionSavingsValueEl = subscriptionSavingsElement.querySelector('.cart-value');
                    if (subscriptionSavingsValueEl) subscriptionSavingsValueEl.textContent = '';
                    subscriptionSavingsElement.style.display = 'none';
                }
            }

            //document.getElementById('banner-discount').textContent = `$${(totalDiscount / 100).toFixed(2)}`; // Update discount

            // const discountBanner = document.getElementById('cart-discount-banner');

            // if (totalDiscount > 0) {
            // discountBanner.style.display = 'block'; // Show the banner
            // document.getElementById('banner-discount').textContent = `$${(totalDiscount / 100).toFixed(2)}`; // Update discount
            // } else {
            // discountBanner.style.display = 'none'; // Hide the banner
            // }

            // 1) Detect subscription items:
            const hasSubscriptionItem = cart.items.some(item =>
                Boolean(item.selling_plan_allocation?.selling_plan)
            );

            // 1a) Debug:
            // if (hasSubscriptionItem) {
            //     console.log('🟢 Subscription product detected in cart');
            // } else {
            //     console.log('🔴 No subscription products in cart');
            // }

            // const discountBanner = document.getElementById('cart-discount-banner');
            // const bannerMainText = document.getElementById('banner-main-text');
            // const progressBar = document.getElementById('cart-progress-bar');

            // const threshold = 4900; // $49.00 in cents
            // if (hasSubscriptionItem) {
            //     bannerMainText.innerHTML     = "🎉 Congratulations! You've got free shipping!";
            //     progressBar.style.width       = `100%`;
            //     discountBanner.style.display  = 'block';

            // } else if (finalTotal === 0) {
            //     bannerMainText.innerHTML = "Free shipping for orders over <strong>$49.00</strong>!";
            //     progressBar.style.width = `0%`;
            //     discountBanner.style.display = 'block';
            // } else if (finalTotal < threshold) {
            //     const difference = ((threshold - finalTotal) / 100).toFixed(2);
            //     bannerMainText.innerHTML = `You’re only <strong>$${difference}</strong> away from free shipping!`;
            //     const percent = Math.min((finalTotal / threshold) * 100, 100);
            //     progressBar.style.width = `${percent}%`;
            //     discountBanner.style.display = 'block';
            // } else {
            //     bannerMainText.innerHTML = "🎉 Congratulations! You've got free shipping!";
            //     progressBar.style.width = `100%`;
            //     discountBanner.style.display = 'block';
            // }

            const discountBanner = document.getElementById('cart-discount-banner');
            const bannerMainText = document.getElementById('banner-main-text');
            const cartProgressContainer = document.getElementById('cart-progress-container');
            const progressBar = document.getElementById('cart-progress-bar');
            const threshold = 4900; // $49.00 in cents

            if (/*hasSubscriptionItem || */finalTotal >= threshold) {
                // Subscription OR cart total ≥ threshold
                bannerMainText.innerHTML = '<div class="fw-bold mb-2 tw:text-xl tw:flex tw:items-center tw:justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="currentColor" class="bi bi-check-circle-fill tw:text-[#A26A21] tw:mr-3" viewBox="0 0 16 16"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/></svg>FREE SHIPPING INCLUDED</div> <div>Roasted fresh <span class="tw:text-[#A26A21]">•</span> Ships next business day.</div>';
                progressBar.style.width = `100%`;
                cartProgressContainer.style.display = 'none'; // Hide progress container

            } else if (finalTotal === 0) {
                // Empty cart
                bannerMainText.innerHTML = "Free shipping for orders over <strong>$49.00</strong>!";
                progressBar.style.width = `0%`;
                cartProgressContainer.style.display = 'block'; // Show progress container

            } else {
                // Cart total below threshold
                const difference = ((threshold - finalTotal) / 100).toFixed(2);
                bannerMainText.innerHTML = `You’re only <strong>$${difference}</strong> away from free shipping!`;
                const percent = Math.min((finalTotal / threshold) * 100, 100);
                progressBar.style.width = `${percent}%`;
                cartProgressContainer.style.display = 'block'; // Show progress container
            }

            // 3) Show the banner
            discountBanner.style.display = 'block';

            // Attach event listeners to quantity and remove buttons
            document.querySelectorAll('.quantity-increase').forEach(button => {
                button.addEventListener('click', () => {
                    if (button.disabled || button.dataset.quantityLocked === 'true') {
                        return;
                    }

                    const variantId = button.dataset.variantId;
                    const sellingPlanId = button.dataset.sellingPlanId || null; // Get selling plan ID or set to null if not present
                    updateQuantity(variantId, 1, sellingPlanId);
                });
            });

            document.querySelectorAll('.quantity-decrease').forEach(button => {
                button.addEventListener('click', () => {
                    if (button.disabled || button.dataset.quantityLocked === 'true') {
                        return;
                    }

                    const variantId = button.dataset.variantId;
                    const sellingPlanId = button.dataset.sellingPlanId || null;
                    updateQuantity(variantId, -1, sellingPlanId);
                });
            });

            document.querySelectorAll('.side-cart-remove').forEach(button => {
                button.addEventListener('click', () => {
                    const variantId = button.dataset.variantId;
                    const sellingPlanId = button.dataset.sellingPlanId || null;
                    removeItem(variantId, sellingPlanId);
                });
            });

            var cartQuantityElement = document.querySelector('.site-header-cart--count');
            if (cartQuantityElement) {
                cartQuantityElement.setAttribute('data-header-cart-count', totalQuantity);
            }


        } catch (error) {
            //console.error('Error loading cart:', error);
        } finally {
            hideOverlay(); // Hide overlay after loading cart
        }
    }

    // Update Item Quantity
    async function updateQuantity(variantId, quantityChange, sellingPlanId = null) {
        showOverlay(); // Show overlay when updating quantity

        try {
            // Fetch current cart data
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Failed to retrieve cart data');
            const cart = await response.json();

            // Find the line number of the item in the cart
            const lineItem = cart.items.find((item, index) =>
                item.id == variantId &&
                ((sellingPlanId && item.selling_plan_allocation?.selling_plan?.id == sellingPlanId) ||
                    (!sellingPlanId && !item.selling_plan_allocation))
            );

            if (!lineItem) {
                //console.error("Item not found in cart for update.");
                hideOverlay();
                return;
            }

            if (isQuantityLocked(lineItem)) {
                hideOverlay();
                return;
            }

            const lineNumber = cart.items.indexOf(lineItem) + 1; // line numbers are 1-based
            const newQuantity = lineItem.quantity + quantityChange;

            // If new quantity is zero or less, remove the item
            if (newQuantity <= 0) {
                await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ line: lineNumber, quantity: 0 })
                });
                await removeShippingProtectionIfAlone();
                // Re-sync Shipping Protection's selling plan against what's left in the cart --
                // e.g. removing the last subscription item should drop it back to one-time.
                await ensureShippingProtection();
                loadCart(); // Reload cart after removing item
                return;
            }

            // Update the quantity for the item at the specified line number
            const updateResponse = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line: lineNumber,
                    quantity: newQuantity
                }),
            });

            if (!updateResponse.ok) throw new Error('Failed to update cart item');
            loadCart(); // Reload cart after updating quantity
            //Update the quantity in the header icon

            //var cartQuantityElement = document.querySelector('.site-header-cart--quantity');


        } catch (error) {
            //console.error('Error updating quantity:', error);
            showCartError(error.message || 'Something went wrong updating your cart. Please try again.');
        } finally {
            hideOverlay(); // Hide overlay after updating quantity
        }
    }

    // Remove Item from Cart
    async function removeItem(variantId, sellingPlanId = null) {
        if (isShippingProtectionItem({ id: variantId })) {
            // Shipping Protection can't be removed directly -- it only clears once it's the last
            // item left in the cart (handled by removeShippingProtectionIfAlone()).
            return;
        }

        showOverlay(); // Show overlay when removing item
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Failed to retrieve cart data');
            const cart = await response.json();

            const lineItem = cart.items.find((item) =>
                item.id == variantId &&
                ((sellingPlanId && item.selling_plan_allocation?.selling_plan?.id == sellingPlanId) ||
                    (!sellingPlanId && !item.selling_plan_allocation))
            );

            if (!lineItem) {
                hideOverlay();
                return;
            }

            const lineNumber = cart.items.indexOf(lineItem) + 1;

            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line: lineNumber,
                    quantity: 0
                }),
            });

            await removeShippingProtectionIfAlone();
            // Re-sync Shipping Protection's selling plan against what's left in the cart -- e.g.
            // removing the last subscription item should drop it back to one-time.
            await ensureShippingProtection();
            loadCart(); // Reload cart after removing item
        } catch (error) {
            //console.error('Error removing item:', error);
            showCartError(error.message || 'Something went wrong removing this item. Please try again.');
        } finally {
            hideOverlay(); // Hide overlay after removing item
        }
    }

    // Add to Cart Functionality
    async function addToCart(variantId, quantity = 1, selling_plan_id = '', subscription = true, lineItemProperties = {}) {
        showOverlay(); // Show overlay when adding item to cart

        try {

            //const subtitleInput = document.querySelector('input[name="properties[type]"]');
            //const caffeineContentInput = document.querySelector('input[name="properties[caffeine]"]');

            const cartData = {
                id: variantId,
                quantity: quantity,
                properties: {
                    ...lineItemProperties
                }
            };

            console.log('Cart payload:', cartData);
            console.log('Subtitle to send:', cartData.properties.Grind);

            // Check if selling_plan exists, and include it in the request if so

            if (subscription) {
                const sellingPlanId = document.querySelector('.appstle_sub_widget')?.getAttribute('data-selected-selling-plan');
                if (sellingPlanId) {
                    cartData.selling_plan = sellingPlanId;
                }
            }

            //TODO: remove .appstle_sub_widget from subscription page
            if (selling_plan_id) {
                cartData.selling_plan = selling_plan_id;
            }

            //console.log(cartData);

            const isProtectionBeingAdded = isShippingProtectionItem({ id: variantId });
            if (!isProtectionBeingAdded) {
                // Add Shipping Protection first (only if it isn't already in the cart) so that
                // this real product lands as the newer line and Protection stays at the bottom.
                await ensureShippingProtectionBeforeAdd(cartData.selling_plan || null);
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cartData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.description || 'Failed to add item to cart');
            }

            if (!isProtectionBeingAdded) {
                await ensureShippingProtection(cartData.selling_plan || null);
            }

            // Open the side cart after adding the item
            toggleCart(true);
            loadCart(); // Reload cart contents

            // Reddit pixel - add to cart event
            try {
                const updatedCart = await fetch('/cart.js').then((res) => res.json());
                const addedItem = updatedCart.items.find((item) => item.variant_id == variantId);

                if (addedItem) {
                    const eventData = {
                        value: (addedItem.line_price / 100).toFixed(2),
                        currency: updatedCart.currency,
                        item_id: addedItem.product_id,
                        item_name: addedItem.title,
                        quantity: addedItem.quantity
                    };

                    // Log all data in a table
                    //console.table([eventData]);

                    rdt('track', 'AddToCart', eventData);
                    console.log('✅ Reddit pixel AddToCart event successfully fired!', addedItem);
                } else {
                    console.warn('⚡ Did not find matching item in updatedCart.');
                }
            } catch (error) {
                console.error('Error while sending addToCart event to Reddit pixel!', error);
            }
            // /Reddit pixel - add to cart event

        } catch (error) {
            console.error('Error adding to cart:', error);
            showCartError(error.message || 'Something went wrong adding this item to your cart. Please try again.');
        } finally {
            hideOverlay(); // Hide overlay after adding item
        }
    }

    // Show overlay
    function showOverlay() {
        document.getElementById('cart-overlay').classList.add('show');
    }

    // Hide overlay
    function hideOverlay() {
        document.getElementById('cart-overlay').classList.remove('show');
    }

    // Show a brief, dismissible error message to the shopper (e.g. when a cart request fails
    // server-side, such as trying to add more units than are actually in stock). There was
    // previously no visible feedback for this at all -- failures were only logged to the
    // console, so the shopper would just see nothing happen when clicking a button.
    function showCartError(message) {
        let toast = document.getElementById('cart-error-toast');
        if (!toast) {
            toast = document.createElement('div');
            toast.id = 'cart-error-toast';
            toast.setAttribute('role', 'alert');
            toast.style.cssText = [
                'position:fixed',
                'bottom:20px',
                'left:50%',
                'transform:translateX(-50%)',
                'background:#c0392b',
                'color:#fff',
                'padding:12px 20px',
                'border-radius:8px',
                'font-size:14px',
                'font-weight:600',
                'z-index:99999',
                'box-shadow:0 4px 12px rgba(0,0,0,0.2)',
                'max-width:90vw',
                'text-align:center',
                'transition:opacity 200ms ease-in-out',
                'opacity:0',
                'pointer-events:none',
            ].join(';');
            document.body.appendChild(toast);
        }

        toast.textContent = message;
        requestAnimationFrame(() => {
            toast.style.opacity = '1';
        });

        clearTimeout(toast._hideTimeout);
        toast._hideTimeout = setTimeout(() => {
            toast.style.opacity = '0';
        }, 4000);
    }

    // Re-evaluates which of the 1/3/6-bag quantity options are actually purchasable for a given
    // variant's inventory count, and re-selects a fallback option if the previously-selected
    // quantity is no longer available. Mirrors the server-rendered logic in
    // snippets/espresso-step-3.liquid, which handles the initial page load; this handles
    // switching to a different grind (a different variant, possibly with different stock)
    // without a page reload.
    function updateStep3InventoryAvailability(form, inventoryQty, isManaged) {
        const cards = form.querySelectorAll('[data-step-3-card]');
        if (!cards.length) return;

        let needsFallbackSelection = false;
        cards.forEach((card) => {
            const qty = Number(card.getAttribute('data-qty') || card.getAttribute('data-quantity') || 0);
            const outOfStock = isManaged && inventoryQty < qty;
            card.classList.toggle('tw:opacity-50', outOfStock);
            if (outOfStock) {
                card.setAttribute('aria-disabled', 'true');
                if (card.classList.contains('is-selected') || card.classList.contains('items-quantity-selector__item--selected')) {
                    needsFallbackSelection = true;
                }
            } else {
                card.removeAttribute('aria-disabled');
            }
        });

        if (!needsFallbackSelection) return;

        const firstAvailable = Array.from(cards).find((c) => c.getAttribute('aria-disabled') !== 'true');
        if (firstAvailable) {
            firstAvailable.click();
        } else {
            // Every quantity option is out of stock for this variant.
            cards.forEach((c) => c.classList.remove('is-selected', 'items-quantity-selector__item--selected'));
        }
    }

    // Grind card selection on product page
    document.body.addEventListener('click', function (e) {
        const grindCard = e.target.closest('form[data-product-form] [data-step-2-card]');
        if (!grindCard) return;
        if (grindCard.getAttribute('aria-disabled') === 'true') return;

        const form = grindCard.closest('form[data-product-form]');
        if (!form) return;

        // Update selected state
        form.querySelectorAll('[data-step-2-card]').forEach(c => c.classList.remove('is-selected'));
        grindCard.classList.add('is-selected');

        // Update hidden variant select for form submission
        const variantId = grindCard.dataset.variantId;
        const variantSelect = form.querySelector('select[name="id"]');
        if (variantSelect && variantId) {
            variantSelect.value = variantId;
        }

        // Refresh ATC button data-variant-id
        updateVariantId(grindCard);

        // Grind cards are only clickable when their variant is available (blocked above via
        // aria-disabled), so reaching this point means the newly selected variant is in stock.
        // Reveal the quantity/purchase options and hide the out-of-stock message, in case the
        // page loaded with an unavailable default variant selected.
        const outOfStockMessage = form.querySelector('[data-step-3-out-of-stock]');
        const quantityCards = form.querySelector('[data-step-3-cards]');
        const purchaseControls = form.querySelector('[data-step-3-purchase-controls]');
        if (outOfStockMessage) outOfStockMessage.style.display = 'none';
        if (quantityCards) quantityCards.style.display = '';
        if (purchaseControls) purchaseControls.style.display = '';

        // Re-check which quantity options this newly selected variant can actually fulfill.
        const inventoryQty = Number(grindCard.dataset.inventoryQty || 0);
        const inventoryManaged = grindCard.dataset.inventoryManaged === 'true';
        updateStep3InventoryAvailability(form, inventoryQty, inventoryManaged);
    });

    if (!document.body.classList.contains('product.new-subscription')) {
        // Attach event listeners to "Add to Cart" buttons
        document.body.addEventListener('click', (event) => {
            // Check if the clicked element is an item selector
            if (event.target.closest('.items-quantity-selector__item')) {
                const selectedItem = event.target.closest('.items-quantity-selector__item');
                if (selectedItem.getAttribute('aria-disabled') === 'true') return;
                const quantitySelector = selectedItem.closest('.items-quantity-selector');

                // Remove the selected class from sibling items only
                quantitySelector?.querySelectorAll('.items-quantity-selector__item').forEach(item => {
                    item.classList.remove('items-quantity-selector__item--selected');
                });

                selectedItem.classList.add('items-quantity-selector__item--selected');

                // Get the quantity directly from the data-quantity attribute
                const quantity = selectedItem.dataset.quantity;

                // Update the Add to Cart button behavior
                const addToCartButton = getAddToCartButton(selectedItem);
                if (addToCartButton) {
                    addToCartButton.dataset.quantity = quantity; // Store the selected quantity
                }
            }

            // Check if the clicked element is the "Add to Cart" button (or a child of it)
            const addBtn = event.target.closest('.product-form-add-to-cart');
            if (addBtn) {
                event.preventDefault();
                // Prevent the enclosing form from submitting as a fallback
                const enclosingForm = addBtn.closest('form');
                if (enclosingForm) {
                    enclosingForm.addEventListener('submit', (e) => e.preventDefault(), { once: true });
                }

                // Guard and read variant & quantity (fall back to sensible defaults)
                const variantId = updateVariantId(addBtn) || resolveVariantId(addBtn);
                let quantity = addBtn.dataset.quantity || addBtn.getAttribute('data-quantity') || 1;
                quantity = parseInt(quantity, 10) || 1;

                if (!variantId) {
                    console.error('Unable to resolve variant ID for add-to-cart button.', addBtn);
                    return;
                }

                // Add a processing class so existing CSS/JS shows the spinner, and ensure it's removed
                addBtn.classList.add('processing');

                // Call addToCart and always clear processing state afterwards
                addToCart(variantId, quantity, '', true, getLockCartQuantityProperties(addBtn))
                    .catch((err) => {
                        console.error('addToCart failed:', err);
                    })
                    .finally(() => {
                        addBtn.classList.remove('processing');
                    });
            }
        });

    }

    // SUBSCRIBE PAGE

    if (document.body.classList.contains('product.new-subscription')) {
        document.getElementById('subscription-button').addEventListener('click', (event) => {
            const subscriptionButton = event.currentTarget;
            addToCart(
                subscriptionButton.getAttribute('data-variant-id'),
                subscriptionButton.getAttribute('data-quantity'),
                subscriptionButton.getAttribute('data-selling-plan-id'),
                true,
                getLockCartQuantityProperties(subscriptionButton)
            );
        });
    }


    // });



    // This code is only for product pages

    // document.addEventListener('DOMContentLoaded', function () {

    updateVariantId();

    if (!document.body.classList.contains('product.new-subscription')) {

        // Prevent native form submission for product forms (avoid full-page /cart redirects)
        document.body.addEventListener('submit', (e) => {
            const form = e.target;
            if (form && form.hasAttribute && form.hasAttribute('data-product-form')) {
                e.preventDefault();
            }
        }, true);

        // Function to update prices based on subscription
        function updatePricesBasedOnSubscription() {
            const appstleSubWidget = document.querySelector('.appstle_sub_widget');
            if (!appstleSubWidget) return;

            const selectedSellingPlan = appstleSubWidget.getAttribute('data-selected-selling-plan');
            const isSubscription = !!selectedSellingPlan;

            const quantitySelectorElement = document.querySelector('.items-quantity-selector');
            if (quantitySelectorElement) {
                quantitySelectorElement.setAttribute('data-purchase-type', isSubscription ? 'subscription' : 'one-time');
            }

            // Update step-3 card prices using the same cents-based logic as the landing page
            const productPriceInput = document.getElementById('product-price');
            if (!productPriceInput) return;

            const productPriceCents = parseFloat(productPriceInput.value);
            const cardsContainer = document.querySelector('[data-step-3-cards]');
            // Same dataset quirk as the other occurrence of this pattern in this file --
            // data-pack-discount-3's trailing digit means dataset.packDiscount3 never matches,
            // so read the attribute directly instead.
            const packDiscount3Cents = Number(cardsContainer?.getAttribute('data-pack-discount-3') || 3) * 100;
            const packDiscount6Cents = Number(cardsContainer?.getAttribute('data-pack-discount-6') || 4) * 100;

            document.querySelectorAll('[data-step-3-card]').forEach(card => {
                const cardQty = parseInt(card.dataset.quantity || card.dataset.qty || '1', 10) || 1;
                const packDiscount = cardQty === 3 ? packDiscount3Cents : cardQty === 6 ? packDiscount6Cents : 0;
                const basePerBagCents = Math.max(productPriceCents - packDiscount, 0);
                const baseLineCents = basePerBagCents * cardQty;
                const totalLineCents = isSubscription ? Math.round(baseLineCents * 0.9) : baseLineCents;
                const perBagCents = isSubscription ? Math.round(basePerBagCents * 0.9) : basePerBagCents;

                const totalNode = card.querySelector('[data-step3-total]');
                const perBagNode = card.querySelector('[data-step3-perbag]');
                if (totalNode) totalNode.textContent = `$${(totalLineCents / 100).toFixed(2)}`;
                if (perBagNode) perBagNode.textContent = `$${(perBagCents / 100).toFixed(2)}`;
            });
        }


        // This event will be fired when a subscription plan is selected
        // document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', function() {
        //     console.log('selling plan', jQuery('.appstle_sub_widget input[name=selling_plan]:checked').val());
        //     // Do your custom actions here
        // });











        /* V1 
        
        // Create a MutationObserver to monitor the addition of .appstle_sub_widget and changes in its attributes
        const observer = new MutationObserver(function (mutationsList, observer) {
            mutationsList.forEach(mutation => {
                // Check if the appstle_sub_widget element was added or if the selected plan attribute has changed
                if (mutation.type === 'childList' || (mutation.type === 'attributes' && mutation.attributeName === 'data-selected-selling-plan')) {
                    const appstleSubWidget = document.querySelector('.appstle_sub_widget');
                    if (appstleSubWidget) {
                        //console.log("Mutation detected, updating prices.");
                        // ?! it's called 100+ times
                        //console.log("Mutation detected, updating prices.");
                        updatePricesBasedOnSubscription();
                    }
                }
            });
        });

        // Observe the body for changes (detects when the appstle_sub_widget is added to the page)
        observer.observe(document.body, { childList: true, subtree: true });
        //observer.observe( document.querySelector('.product-main'), { childList: true, subtree: true });
       

        // Additionally, observe the .appstle_sub_widget for attribute changes (especially data-selected-selling-plan)
        const appstleSubWidget = document.querySelector('.appstle_sub_widget');
        if (appstleSubWidget) {
            //console.log("Observing .appstle_sub_widget for changes.");
            observer.observe(appstleSubWidget, { attributes: true, attributeFilter: ['data-selected-selling-plan'] });
        }

        // Initial check to update prices if the appstle_sub_widget is already on the page
        setTimeout(() => {
            const appstleSubWidget = document.querySelector('.appstle_sub_widget');
            if (appstleSubWidget) {
                //console.log("Initial load detected, updating prices.");
                updatePricesBasedOnSubscription();
            }
        }, 1000); // Delay to ensure the element is available after initial page load

         /V1 */


        /* V2 */
        jQuery(document).ready(function ($) {
            // Function to update prices
            function updatePrices() {
                var appstleSubWidget = $('.appstle_sub_widget');
                if (appstleSubWidget.length) {
                    updatePricesBasedOnSubscription();
                } else {
                    //setTimeout(updatePrices, 500);
                    console.log('not found');
                }
            }

            document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', function () {
                //console.log('selling plan', jQuery('.appstle_sub_widget input[name=selling_plan]:checked').val());
                updatePrices();
                updateVariantId();
            });

            // Initial check for the widget presence
            updatePrices();

            // Use jQuery event delegation to handle changes to .appstle_sub_widget or its attributes
            var observerTarget = $('body'); // Replace with the actual container selector
            if (observerTarget.length) {
                // Monitor the .appstle_sub_widget for changes
                observerTarget.on('change', '.appstle_sub_widget', function () {
                    updatePrices();
                    console.log('change');
                });

                // Monitor the variant selection
                observerTarget.on('change', '.variant-selection', function () {
                    updateVariantId(this);
                    console.log('change variant');
                });

                //

                // Also, listen for DOM modifications that add or remove elements
                // observerTarget.on('DOMNodeInserted', function (event) {
                //     if ($(event.target).hasClass('appstle_sub_widget')) {
                //         updatePrices();
                //     }
                // });

                // observerTarget.on('DOMNodeRemoved', function (event) {
                //     if ($(event.target).hasClass('appstle_sub_widget')) {
                //         updatePrices();
                //     }
                // });

                // Monitor attribute changes, specifically for data-selected-selling-plan
                // observerTarget.on('DOMAttrModified', '.appstle_sub_widget[data-selected-selling-plan]', function () {
                //     updatePrices();
                // });

                // Monitor Quick Buy modal opening
                $(document).on('modal-opened', '.modal--quickshop-full', function () {
                    //console.log('Quick Buy modal opened');
                    //updatePrices();
                });

            }

            // Initial check after a delay
            setTimeout(updatePrices, 1000); // Delay to ensure the element is available after page load




        });

        /* /V2 */




        /* V3 
        // Function to update prices
        function updatePrices() {
            var appstleSubWidget = document.querySelectorAll('.appstle_sub_widget');
            if (appstleSubWidget.length > 0) {
                updatePricesBasedOnSubscription();
            }
        }
    
        // Initial check for the widget presence
        updatePrices();
    
        // Use MutationObserver for handling changes to .appstle_sub_widget or its attributes
        var observerTarget = document.querySelector('body'); // Replace with the actual container selector
        if (observerTarget) {
            var observer = new MutationObserver(function (mutationsList) {
                mutationsList.forEach(function (mutation) {
                    if (mutation.type === 'childList' || (mutation.type === 'attributes' && mutation.attributeName === 'data-selected-selling-plan')) {
                        var appstleSubWidget = document.querySelector('.appstle_sub_widget');
                        if (appstleSubWidget) {
                            updatePrices();
                        }
                    }
                });
            });
    
            observer.observe(observerTarget, { childList: true, subtree: true });
    
            // Monitor attribute changes specifically for data-selected-selling-plan
            observer.observe(document.querySelector('.appstle_sub_widget'), { attributes: true, attributeFilter: ['data-selected-selling-plan'] });
        }
    
        // Initial check after a delay
        setTimeout(updatePrices, 1000); // Delay to ensure the element is available after page load
    
        /V3 */








    }





    /*
    function upsell_control() {
        if (sessionStorage.getItem('inLegendaryFunnel') === 'true') {
            console.log('✅ inLegendaryFunnel is set to true');
    
            var checkoutButton = document.querySelector('#checkout-button');
    
            if (checkoutButton) {
                checkoutButton.addEventListener('click', function (event) {
                    event.preventDefault();
                    console.log('🛑 Default checkout prevented. Checking if subscription product is in cart.');
    
                    // Step 1: Check if the subscription product is already in the cart
                    fetch('/cart.js')
                        .then(response => response.json())
                        .then(cart => {
                            //const isSubscriptionProductInCart = cart.items.some(item => item.variant_id === 7110423773287);
                            const isCartEmpty = !cart.items || cart.items.length === 0;
                            const isSubscriptionProductInCart = cart.items.some(item => item.product_id === 7110423773287);
                            
                            if (isSubscriptionProductInCart || isCartEmpty) {
                                console.log('⚠️ Subscription product already in the cart. Skipping upsell modal.');
                                // Proceed to checkout without showing the modal
                                //window.location.href = '/checkout';
                            } else {
                                console.log('🛑 Subscription product not in cart. Opening Legendary Funnel.');
                                // Open the Legendary Funnel modal
                                openCustomModal({
                                    contentElement: '#legendary-9-funnel'
                                });
    
                                // Add event listener for #claim-my-legends after modal content is rendered
                                setTimeout(function () {
                                    var claimButton = document.querySelector('#claim-my-legends');
                                    if (claimButton) {
                                        claimButton.addEventListener('click', function () {
                                            console.log('🏆 Claim button clicked. Clearing cart and adding product.');
    
                                            // Step 1: Clear the cart first
                                            fetch('/cart/clear.js', { method: 'POST' })
                                                .then(response => response.json())
                                                .then(() => {
                                                    console.log('🧹 Cart cleared.');
    
                                                    // Step 2: Add product to cart with selling plan
                                                    addToCart(40993123991655, 1, 10029989991)
                                                        .then(() => {
                                                            // Step 3: Close the modal
                                                            closeCustomModal();
                                                            console.log('🚪 Modal closed.');
    
                                                            // Step 4: Redirect to checkout
                                                            //window.location.href = '/checkout';
                                                        })
                                                        .catch(error => {
                                                            console.error('Error adding to cart:', error);
                                                        });
                                                })
                                                .catch(error => {
                                                    console.error('Error clearing cart:', error);
                                                });
                                        });
                                    } else {
                                        console.log('⚠️ Claim button not found inside modal.');
                                    }
    
                                    // Add listener for #do-not-claim-my-legends inside the modal
                                    var doNotClaimButton = document.querySelector('#do-not-claim-my-legends');
                                    if (doNotClaimButton) {
                                        doNotClaimButton.addEventListener('click', function () {
                                            console.log('🚫 Do not claim button clicked. Closing current modal and opening downsell funnel.');
    
                                            // Step 1: Close the current modal
                                            closeCustomModal();
                                            console.log('🚪 Current modal closed.');
    
                                            // Step 2: Open the downsell funnel modal
                                            openCustomModal({
                                                contentElement: '#bundle-downsell-funnel'
                                            });
    
                                            // Add listeners for the downsell modal
                                            setTimeout(function () {
                                                // --- Add listener for #claim-my-3-bag-deal inside the downsell modal ---
                                                var claim3BagDealButton = document.querySelector('#claim-my-3-bag-deal');
                                                if (claim3BagDealButton) {
                                                    claim3BagDealButton.addEventListener('click', function () {
                                                        console.log('🎁 3-Bag Deal claimed. Checking current cart.');
    
                                                        // Step 1: Get the current cart content
                                                        fetch('/cart.js')
                                                            .then(response => response.json())
                                                            .then(cart => {
                                                                const lastItem = cart.items[cart.items.length - 1]; // Last item in cart
                                                                const lastProductId = lastItem ? lastItem.variant_id : null;
    
                                                                if (lastProductId) {
                                                                    // Step 2: If there are less than 3 of the last product, add the required quantity
                                                                    const quantityToAdd = lastItem.quantity < 3 ? 3 - lastItem.quantity : 0;
    
                                                                    if (quantityToAdd > 0) {
                                                                        console.log(`Adding ${quantityToAdd} more of product with variant ID ${lastProductId}.`);
    
                                                                        addToCart(parseInt(lastProductId), parseInt(quantityToAdd))
                                                                            .then(() => {
                                                                                console.log(`Added ${quantityToAdd} more products to the cart.`);
                                                                            })
                                                                            .catch(error => {
                                                                                console.error('Error adding products to cart:', error);
                                                                            });
                                                                    } else {
                                                                        console.log('Already have 3 or more of this product.');
                                                                    }
                                                                } else {
                                                                    console.log('❌ No last product found in the cart.');
                                                                }
                                                            })
                                                            .catch(error => {
                                                                console.error('Error fetching cart:', error);
                                                            });
    
                                                        // Step 3: Close the modal and redirect to checkout
                                                        closeCustomModal();
                                                        console.log('🚪 Modal closed.');
    
                                                        // Step 4: Redirect to checkout
                                                        //window.location.href = '/checkout';
                                                    });
                                                } else {
                                                    console.log('⚠️ Claim 3-bag deal button not found inside downsell modal.');
                                                }
    
                                                // --- Add listener for #do-not-claim-my-3-bag-deal inside the downsell modal ---
                                                var doNotClaim3BagDealButton = document.querySelector('#do-not-claim-my-3-bag-deal');
                                                if (doNotClaim3BagDealButton) {
                                                    doNotClaim3BagDealButton.addEventListener('click', function () {
                                                        console.log('🚫 Do not claim 3-bag deal clicked. Closing downsell modal and redirecting to checkout.');
    
                                                        // Step 1: Close the downsell modal
                                                        closeCustomModal();
                                                        console.log('🚪 Downsell modal closed.');
    
                                                        // Step 2: Redirect to checkout
                                                        //window.location.href = '/checkout';
                                                    });
                                                } else {
                                                    console.log('⚠️ Do not claim 3-bag deal button not found inside downsell modal.');
                                                }
                                            }, 300); // slight delay to ensure modal content is rendered
                                        });
                                    } else {
                                        console.log('⚠️ Do not claim button not found inside modal.');
                                    }
                                }, 300); // slight delay to ensure modal content is rendered
                            }
                        })
                        .catch(error => {
                            console.error('Error fetching cart:', error);
                        });
                });
            } else {
                console.log('⚠️ Checkout button not found.');
            }
    
        } else {
            console.log('❌ inLegendaryFunnel is not set or not true');
        }
    }
    
    upsell_control();

    */



    function setCookie(name, value, duration) {
        const d = new Date();
        d.setTime(d.getTime() + duration * 24 * 60 * 60 * 1000);
        const expires = 'expires=' + d.toUTCString();
        document.cookie = name + '=' + value + ';' + expires + ';path=/';
    }

    function getCookie(name) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i].trim();
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
        }
        return null;
    }

    function updateDownsellImage(productInCart) {
        const metafieldImage = window.ShopifyProductMetafields?.[productInCart.handle] || null;
        const downsellImage = document.querySelector('#bundle-downsell-image');
        if (downsellImage) {
            downsellImage.src = metafieldImage || productInCart.featured_image?.url || '';
        }
    }

    function upsell_control() {
        console.log('✅ inLegendaryFunnel is set to true');

        const checkoutButton = document.querySelector('#checkout-button');
        if (!checkoutButton) {
            console.log('⚠️ Checkout button not found.');
            return;
        }

        // Global event listener for modal buttons
        document.body.addEventListener('click', async function (e) {
            if (e.target && e.target.id === 'claim-my-legends') {
                console.log('🧹 Clearing cart and adding subscription product...');
                closeCustomModal();

                const cart = await fetch('/cart.js').then(res => res.json());
                const cartItem = cart.items[0];
                const selectedTitle = cartItem?.variant_title || cartItem?.options_with_values?.[0]?.value;
                console.log(`Variant title from cart: [${selectedTitle}]`);

                const variantMap = {
                    'Whole Bean': 40993123991655,
                    'Ground': 40993124024423,
                    'Pod Grind': 40993124057191,
                    'Espresso Grind': 41307215790183
                };

                const matchedVariantId = variantMap[selectedTitle?.trim()];

                if (matchedVariantId) {
                    await fetch('/cart/clear.js', { method: 'POST' });
                    await addToCart(matchedVariantId, 1, 10030022759, true);
                    window.location.href = '/checkout';
                } else {
                    console.error('❌ No matching variant found for subscription.');
                }
            }

            if (e.target.closest('#do-not-claim-my-legends')) {
                closeCustomModal();

                // openCustomModal({
                //     contentElement: '#bundle-downsell-funnel',
                //     modalClassName: 'modal-small'
                // });

                try {
                    const cart = await fetch('/cart.js').then(res => res.json());
                    const productInCart = cart.items[0];

                    const metafieldImage = window.ShopifyProductMetafields?.[productInCart.handle] || null;
                    const downsellImage = document.querySelector('#bundle-downsell-image');
                    if (downsellImage) {
                        downsellImage.src = metafieldImage || productInCart.featured_image?.url || '';
                    }

                    const truncateToTwoDecimals = num => Math.floor(num * 100) / 100;
                    const price = productInCart.price / 100;
                    const priceWithDiscount = price * 0.9;
                    const totalOriginal = truncateToTwoDecimals(price * 3);
                    const totalWithDiscount = truncateToTwoDecimals(priceWithDiscount * 3);

                    document.querySelector('#bundle-downsell-original-price').textContent = `$${totalOriginal.toFixed(2)}`;
                    document.querySelector('#bundle-downsell-price-with-discount').textContent = `$${totalWithDiscount.toFixed(2)}`;
                    document.querySelector('#bundle-downsell-price-per-bag-with-discount').textContent = `$${priceWithDiscount.toFixed(2)}`;
                } catch (error) {
                    console.error('❌ Error fetching cart for downsell modal:', error);
                }
            }

            if (e.target && e.target.id === 'claim-my-3-bag-deal') {
                console.log('🎁 3-Bag Deal claimed. Adding more of same product...');
                fetch('/cart.js')
                    .then(res => res.json())
                    .then(async cart => {
                        const productInCart = cart.items[0];
                        const lastProductId = productInCart.id;
                        const quantityToAdd = productInCart.quantity < 3 ? 3 - productInCart.quantity : 0;

                        if (quantityToAdd > 0) {
                            await addToCart(lastProductId, quantityToAdd, false, false);
                            console.log(`✅ Added ${quantityToAdd} more.`);
                        }

                        document.cookie = 'used_3bag_offer=true; path=/; max-age=' + 60 * 60 * 24 * 365;

                        closeCustomModal();
                        applyDiscountCode('3PACK10');
                        window.location.href = '/checkout';
                    });
            }

            if (e.target.closest('#do-not-claim-my-3-bag-deal')) {
                console.log('❌ 3-Bag Deal not claimed. Closing modal.');
                closeCustomModal();
                window.location.href = '/checkout';
            }
        });

        checkoutButton.addEventListener('click', function (event) {
            event.preventDefault();
            window.location.href = '/checkout';
            return;
           

            // Skip modal only if user claimed 3-bag deal AND didn’t come from landing page
            if (
                getCookie('used_3bag_offer') === 'true' &&
                getCookie('legendary_landing_visited') !== 'true'
            ) {
                window.location.href = '/checkout';
                return;
            }

            fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                    const isCartEmpty = !cart.items || cart.items.length === 0;
                    const hasSubscription = cart.items.some(item => item.selling_plan_allocation);

                    if (hasSubscription || isCartEmpty) {
                        console.log('⏭️ Skipping modal - sub in cart or empty.');
                        window.location.href = '/checkout';
                        return;
                    }

                    if (cart.items.length !== 1) {
                        console.log('⏭️ More than 1 product. Skipping modal.');
                        window.location.href = '/checkout';
                        return;
                    }

                    const productInCart = cart.items[0];
                    if (![1, 2].includes(productInCart.quantity)) {
                        console.log('⏭️ Quantity not 1 or 2. Skipping modal.');
                        window.location.href = '/checkout';
                        return;
                    }

                    const metafieldImage = window.ShopifyProductMetafields?.[productInCart.handle] || null;
                    const downsellImage = document.querySelector('#bundle-downsell-image');
                    if (downsellImage) {
                        downsellImage.src = metafieldImage || productInCart.featured_image?.url || '';
                    }

                    const truncateToTwoDecimals = num => Math.floor(num * 100) / 100;
                    const price = productInCart.price / 100;
                    const priceWithDiscount = price * 0.9;
                    const totalOriginal = truncateToTwoDecimals(price * 3);
                    const totalWithDiscount = truncateToTwoDecimals(priceWithDiscount * 3);

                    document.querySelector('#bundle-downsell-original-price').textContent = `$${totalOriginal.toFixed(2)}`;
                    document.querySelector('#bundle-downsell-price-with-discount').textContent = `$${totalWithDiscount.toFixed(2)}`;
                    document.querySelector('#bundle-downsell-price-per-bag-with-discount').textContent = `$${priceWithDiscount.toFixed(2)}`;

                    const productInCartHandle = productInCart.handle;
                    let isLegendaryCollectionProduct = false;
                    if (
                        Array.isArray(window.legendaryHandles) &&
                        window.legendaryHandles.includes(productInCartHandle)
                    ) {
                        isLegendaryCollectionProduct = true;
                    }

                    if (getCookie('legendary_landing_visited') === 'true' && isLegendaryCollectionProduct) {
                        // openCustomModal({
                        //     contentElement: '#legendary-9-funnel',
                        //     modalClassName: 'modal-small'
                        // });

                        const bagText = productInCart.quantity === 1 ? 'one bag' : 'two bags';
                        const quantityElements = document.querySelectorAll('.bags-quantity');
                        quantityElements.forEach(el => (el.textContent = bagText));
                    } else {
                        // openCustomModal({
                        //     contentElement: '#bundle-downsell-funnel',
                        //     modalClassName: 'modal-small'
                        // });
                    }
                })
                .catch(error => {
                    console.error('❌ Error checking cart:', error);
                    window.location.href = '/checkout';
                });
        });
    }

    upsell_control();







    function applyDiscountCode(discountCode) {
        return fetch(`/discount/${discountCode}`)
            .then(function (response) {
                if (!response.ok) {
                    throw new Error(`Failed to apply discount code: ${response.statusText}`);
                }
                return fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        updates: {}, // Optional: Pass updated cart details here if needed
                    }),
                });
            })
            .then(function (cartResponse) {
                if (cartResponse.ok) {
                    console.log(`✅ Discount code ${discountCode} applied successfully and cart updated.`);
                    // Redirect to checkout after discount and cart update
                    window.location.href = '/checkout';
                } else {
                    throw new Error(`Failed to update cart: ${cartResponse.statusText}`);
                }
            })
            .catch(function (error) {
                console.error('Error:', error);
            });

    }

    // attach it to features section button

    // if ( window.location.pathname === "/pages/test-page" ) {
    //     openCustomModal({
    //       contentElement: "#jebbit-survey-1",
    //       modalClassName: "modal-small"
    //     });
    //   }


    // const survey1_btn = document.querySelector(
    //     '.page-id-95718998119 .features-section .features-section-block-1 .button-primary'
    // );

    // if (survey1_btn) {
    //     survey1_btn.addEventListener('click', (e) => {
    //         e.preventDefault();  // <-- stop the link from navigating
    //         openCustomModal({
    //             contentElement: '#jebbit-survey-1',
    //             modalClassName: 'modal-big'
    //         });
    //     });
    // }

    const urlParams = new URLSearchParams(window.location.search);
    if (
        window.location.pathname === '/pages/coffee-history' &&
        urlParams.get('show_survey') === '1'
    ) {
        openCustomModal({
            contentElement: '#jebbit-survey-1',
            modalClassName: 'modal-big'
        });
    }












    //addToCart(, 1,false, false); // Add subscription product to cart on page load
    // add on page load, so we check the url and add item
    // openCustomModal({
    //     contentElement: '#jebbit-survey-1-confirmation',
    //     modalClassName: 'modal-small'
    // });

    // document.querySelectorAll('[data-action="view-cart"]').forEach(button => {
    //     button.addEventListener('click', function (e) {
    //         e.preventDefault(); // Optional: prevent default link behavior
    //         closeCustomModal(); // Close any open modals
    //         toggleCart(true);
    //         loadCart(); // Reload cart contents
    //     });
    // });

    // Listen jebbit messages
    // window.addEventListener("message", event => {
    //     if (event.origin !== "https://quantum-brew.jebbit.com") return;
    //     console.log("Jebbit message:", event.data);
    //   });

    document.querySelectorAll('form[data-product-form]').forEach((form) => {
        updateVariantId(form);
    });







});

jQuery(document).ready(function ($) {

    $('.custom-recipe-slider').slick({
        infinite: true,
        slidesToShow: 4,
        slidesToScroll: 1,
        autoplay: false,
        prevArrow: 'section.custom-recipes-slider .slick-prev', // Link to your custom previous arrow
        nextArrow: 'section.custom-recipes-slider .slick-next', // Link to your custom next arrow
        autoplaySpeed: 7000,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 3
                }
            },
            {
                breakpoint: 720,
                settings: {
                    slidesToShow: 2
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1
                }
            }
        ]
    });

    // Homepage slider 
    $('body.template-index .featured-collection--section .productgrid--items').slick({
        centerMode: false,
        slidesToShow: 4,
        slidesToScroll: 1,
        speed: 800,
        // arrows: true,
        dots: false,
        autoplay: true,
        infinite: true,
        autoplaySpeed: 8000,
        swipe: false,
        prevArrow: '<button class="slick-prev custom-arrow left transition-opacity" aria-label="Previous"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-arrow-left-circle-fill" viewBox="0 0 16 16"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0m3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z"/></svg></button>',
        nextArrow: '<button class="slick-next custom-arrow right transition-opacity" aria-label="Next"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-arrow-right-circle-fill" viewBox="0 0 16 16"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0M4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5z"/></svg></button>',
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: 3
                }
            },
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2
                }
            },
            {
                breakpoint: 720,
                //settings: "unslick"  // Disable slick at 720px and below
                settings: {
                    slidesToShow: 1
                }
            }
        ]
    });

});

jQuery(document).ready(function ($) {
    $('body.page-id-95082807399 .featured-collection--section .productgrid--items, body.page-id-95718998119 .featured-collection--section .productgrid--items')
        .on('init', function (event, slick) {
            setTimeout(() => {
                const firstVisibleProduct = $(this)
                    .find('.slick-slide.slick-active')
                    .first()
                    .find('.productitem')
                    .first();

                if (firstVisibleProduct.length) {
                    firstVisibleProduct.trigger('click');
                }
            }, 100); // Slight delay to ensure Slick has done layout
        })
        .slick({
            centerMode: false,
            slidesToShow: 4,
            slidesToScroll: 1,
            speed: 800,
            dots: false,
            infinite: true,
            autoplaySpeed: 8000,
            swipe: false,
            prevArrow:
                '<button class="slick-prev custom-arrow left transition-opacity" aria-label="Previous"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-arrow-left-circle-fill" viewBox="0 0 16 16"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0m3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z"/></svg></button>',
            nextArrow:
                '<button class="slick-next custom-arrow right transition-opacity" aria-label="Next"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-arrow-right-circle-fill" viewBox="0 0 16 16"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0M4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5z"/></svg></button>',
            responsive: [
                { breakpoint: 1200, settings: { slidesToShow: 3 } },
                { breakpoint: 1024, settings: { slidesToShow: 2 } },
                { breakpoint: 720, settings: { slidesToShow: 1 } },
            ],
        });
});
